/**
 * The Collection's card pages, kept a few at a time (docs/asset-loading.md
 * law 3: preload by intent, and "subsequent pages prefetch at idle priority
 * while the current page is viewed").
 *
 * The server pages by OFFSET over one sorted list
 * (game/utils/catalogPageBuilder.js), so a page is a slice of that list and any
 * page whose slice lies inside an answer already held is cut from it without a
 * request. That is what lets the lobby warm the first page before the
 * Collection has ever been laid out: it asks at the largest page the grid can
 * show, and whatever capacity the grid then measures — five cards on a small
 * phone, six or eight on a desktop — page 1 is a prefix of that answer. The
 * single-slot prefetch this replaces was keyed by the exact page size, so a
 * lobby warm taken at eight missed a grid that measured six, and the open paid
 * a second round trip with eight skeletons on screen.
 *
 * Answers are keyed by everything that decides WHICH cards a page holds (tab,
 * summoner, filters, sort, search, language, account), kept LRU-bounded, and
 * served only while young enough for the caller. A failed read is never kept.
 */

function positiveInteger(value) {
    const numeric = Math.floor(Number(value));
    return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
}

function queryKey(query) {
    const entries = Object.entries(query || {})
        .map(([name, value]) => [name, value == null ? '' : String(value)])
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return JSON.stringify(entries);
}

/**
 * Cut page `page` of `pageSize` out of a server answer, exactly as the server
 * would have served it, or `null` when the answer does not hold all of it.
 */
function derivePage(response, page, pageSize) {
    const size = positiveInteger(pageSize);
    const sourcePage = positiveInteger(response?.page);
    const sourceSize = positiveInteger(response?.pageSize);
    const items = Array.isArray(response?.items) ? response.items : null;
    if (!size || !sourcePage || !sourceSize || !items) return null;
    if (response?.totalsUnknown === true) return deriveUncountedPage(response, items, page, size, sourcePage, sourceSize);
    const total = Number(response?.totalItems);
    if (!Number.isInteger(total) || total < 0) return null;

    const coverStart = (sourcePage - 1) * sourceSize;
    const coverEnd = coverStart + items.length;
    // A short answer is only whole when it runs to the end of the list.
    if (coverEnd !== Math.min(coverStart + sourceSize, total)) return null;

    const totalPages = Math.max(1, Math.ceil(total / size));
    const wanted = Math.min(Math.max(positiveInteger(page) || 1, 1), totalPages);
    const start = (wanted - 1) * size;
    const end = Math.min(start + size, total);
    if (start < coverStart || end > coverEnd) return null;
    return {
        ...response,
        page: wanted,
        pageSize: size,
        totalPages,
        items: items.slice(start - coverStart, end - coverStart),
    };
}

/**
 * The same cut from an uncounted answer (a very large collection, read from
 * its collection index: collection-page-answer.js). It has no total, only
 * whether more follow it, so a page inside it is cut as above, and one that
 * runs past it is held only when nothing follows.
 */
function deriveUncountedPage(response, items, page, size, sourcePage, sourceSize) {
    const coverStart = (sourcePage - 1) * sourceSize;
    const coverEnd = coverStart + items.length;
    const atEnd = response.hasMore !== true;
    // A short answer is only whole when it runs to the end of the list.
    if (items.length < sourceSize && !atEnd) return null;
    const wanted = Math.max(positiveInteger(page) || 1, 1);
    const start = (wanted - 1) * size;
    const end = start + size;
    if (start < coverStart || (end > coverEnd && !atEnd)) return null;
    // Nothing of it is held (past the end): the server says what is there.
    if (start >= coverEnd && !(wanted === 1 && coverEnd === 0)) return null;
    const hasMore = end < coverEnd || !atEnd;
    return {
        ...response,
        page: wanted,
        pageSize: size,
        hasMore,
        totalPages: hasMore ? wanted + 1 : wanted,
        items: items.slice(start - coverStart, Math.min(end, coverEnd) - coverStart),
    };
}

/**
 * @param {{
 *   fetchPage: (query: object, page: number, pageSize: number, options: { refresh: boolean, prefetch: boolean }) => Promise<any>,
 *   warmEntries?: (items: any[], priority: string) => void,
 *   maxEntries?: number,
 *   now?: () => number,
 * }} options
 */
function createCollectionPageCache({
    fetchPage,
    warmEntries = null,
    maxEntries = 6,
    now = () => Date.now(),
} = /** @type {any} */ ({})) {
    /** @type {Map<string, Array<{ page: number, pageSize: number, response: any, fetchedAt: number }>>} */
    let settled = new Map();
    /** @type {Array<{ key: string, page: number, pageSize: number, fetchedAt: () => number, promise: Promise<any> }>} */
    let inFlight = [];
    let epoch = 0;

    function count() {
        let total = 0;
        settled.forEach((list) => { total += list.length; });
        return total;
    }

    function trim() {
        while (count() > maxEntries) {
            // Oldest key first; within a key, the entry stored longest ago.
            const [key, list] = settled.entries().next().value;
            list.shift();
            if (list.length === 0) settled.delete(key);
        }
    }

    function store(key, page, pageSize, response) {
        const list = (settled.get(key) || [])
            .filter((entry) => entry.page !== page || entry.pageSize !== pageSize);
        list.push({ page, pageSize, response, fetchedAt: now() });
        settled.delete(key);
        settled.set(key, list);
        trim();
    }

    function lookup(key, page, pageSize, maxAgeMs) {
        const list = settled.get(key);
        if (!list) return null;
        const age = Number.isFinite(maxAgeMs) ? maxAgeMs : Infinity;
        for (let index = list.length - 1; index >= 0; index -= 1) {
            const entry = list[index];
            if (now() - entry.fetchedAt > age) continue;
            const derived = derivePage(entry.response, page, pageSize);
            if (!derived) continue;
            // Touch: the key is the most recently used again.
            settled.delete(key);
            settled.set(key, list);
            return { page: derived, fetchedAt: entry.fetchedAt };
        }
        return null;
    }

    /**
     * Whether an answer to page `flight.page` at `flight.pageSize` holds page
     * `page` at `pageSize`: the offsets it asks for lie inside the ones it
     * asked for (the lobby's three-page read holds pages two and three).
     */
    function flightCovers(flight, page, pageSize) {
        const start = (page - 1) * pageSize;
        const flightStart = (flight.page - 1) * flight.pageSize;
        return start >= flightStart && start + pageSize <= flightStart + flight.pageSize;
    }

    /**
     * A held page, synchronously, or null.
     * @param {object} query
     * @param {number} page
     * @param {number} pageSize
     * @param {{ maxAgeMs?: number }} [options]
     */
    function peek(query, page, pageSize, { maxAgeMs = Infinity } = {}) {
        return lookup(queryKey(query), page, pageSize, maxAgeMs);
    }

    /**
     * The page, from memory when an answer young enough holds it, else read.
     * Resolves to `{ page, fetchedAt }`, or null when the read failed.
     * @param {object} query
     * @param {number} page
     * @param {number} pageSize
     * @param {{ maxAgeMs?: number, refresh?: boolean, prefetch?: boolean, priority?: string }} [options]
     */
    function request(query, page, pageSize, {
        maxAgeMs = Infinity,
        refresh = false,
        prefetch = false,
        priority = 'low',
    } = {}) {
        const key = queryKey(query);
        if (!refresh) {
            const held = lookup(key, page, pageSize, maxAgeMs);
            if (held) return Promise.resolve(held);
            const joined = inFlight.find((flight) => flight.key === key && flightCovers(flight, page, pageSize));
            if (joined) {
                return joined.promise.then((response) => {
                    const derived = response ? derivePage(response, page, pageSize) : null;
                    if (derived) return { page: derived, fetchedAt: joined.fetchedAt() };
                    return request(query, page, pageSize, { maxAgeMs, prefetch, priority });
                });
            }
        }

        const flightEpoch = epoch;
        let fetchedAt = now();
        const flight = {
            key,
            page,
            pageSize,
            fetchedAt: () => fetchedAt,
            promise: Promise.resolve()
                .then(() => fetchPage(query, page, pageSize, { refresh, prefetch }))
                .then((response) => {
                    fetchedAt = now();
                    if (flightEpoch === epoch && response) store(key, page, pageSize, response);
                    if (prefetch && response && typeof warmEntries === 'function') {
                        try {
                            warmEntries(Array.isArray(response.items) ? response.items : [], priority);
                        } catch (_) { /* a warm is best-effort */ }
                    }
                    return response || null;
                }, () => null)
                .finally(() => { inFlight = inFlight.filter((entry) => entry !== flight); }),
        };
        inFlight.push(flight);
        return flight.promise.then((response) => {
            if (!response) return null;
            const derived = derivePage(response, page, pageSize) || response;
            return { page: derived, fetchedAt };
        });
    }

    /** Forget everything: the next read of any page goes to the server. */
    function invalidate() {
        epoch += 1;
        settled = new Map();
        inFlight = [];
    }

    return { peek, request, invalidate };
}

export { createCollectionPageCache, derivePage, queryKey };
